import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { specialistsApi } from '../../services/apiService';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import Pagination from '../../components/Pagination/Pagination';
import SpecialistEditModal from '../../components/SpecialistEditModal/SpecialistEditModal';
import { SOCKET_URL } from '../../config/api';
import './AdminSpecialistPage.css';
import '../../components/AdminCreateModal.css';

// Helper to resolve image URLs
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${SOCKET_URL}${url}`;
  return url;
};

const AdminSpecialistPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  // Data states
  const [specialists, setSpecialists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryRef = useRef(null);
  
  // Selection states
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Action dropdown state
  const [actionDropdown, setActionDropdown] = useState({ show: false, specialistId: null, x: 0, y: 0 });

  // Details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsSpecialist, setDetailsSpecialist] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Notes modal state
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesSpecialist, setNotesSpecialist] = useState(null);
  const [specialistNotes, setSpecialistNotes] = useState('');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSpecialist, setEditSpecialist] = useState(null);

  // Create modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createErrors, setCreateErrors] = useState({});
  const [createForm, setCreateForm] = useState({
    name: '', email: '', phone: '', title: '', category: '',
    expertise: '', experience: '', country: '', city: '', companyName: '', bio: ''
  });

  const resetCreateForm = () => {
    setCreateForm({ name: '', email: '', phone: '', title: '', category: '', expertise: '', experience: '', country: '', city: '', companyName: '', bio: '' });
    setCreateErrors({});
  };

  const handleCreateSpecialist = async () => {
    const errors = {};
    if (!createForm.name.trim()) errors.name = 'Name is required';
    if (!createForm.email.trim()) errors.email = 'Email is required';
    if (Object.keys(errors).length > 0) { setCreateErrors(errors); return; }
    try {
      setCreateSaving(true);
      await specialistsApi.adminCreate(createForm);
      setShowCreateModal(false);
      resetCreateForm();
      fetchSpecialists();
    } catch (err) {
      setCreateErrors({ submit: err.response?.data?.error || 'Failed to create specialist' });
    } finally {
      setCreateSaving(false);
    }
  };

  const categoryOptions = [
    'Architecture', 'Civil Engineering', 'Electrical', 'Mechanical',
    'Structural Engineering', 'Interior Design', 'Landscape Architecture',
    'Environmental Engineering', 'Project Management', 'Urban Planning',
    'Construction Management', 'MEP Engineering'
  ];

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch specialists from API
  const fetchSpecialists = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await specialistsApi.getAllAdmin({ limit: 200 });
      const specialistData = Array.isArray(response) ? response : (response.specialists || []);
      setSpecialists(specialistData);
    } catch (err) {
      console.error('Error fetching specialists:', err);
      setError('Failed to fetch specialists');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchSpecialists();
  }, [fetchSpecialists]);

  // Get unique values for filters
  const uniqueCountries = [...new Set(specialists.map(s => s.country).filter(Boolean))];
  const allCategories = [...new Set([...categoryOptions, ...specialists.map(s => s.category).filter(Boolean)])];

  // Filter specialists
  const filteredSpecialists = specialists.filter(specialist => {
    const q = debouncedSearch.toLowerCase();
    const matchesSearch = !q || 
      specialist.name?.toLowerCase().includes(q) ||
      specialist.email?.toLowerCase().includes(q) ||
      specialist.country?.toLowerCase().includes(q) ||
      specialist.city?.toLowerCase().includes(q) ||
      specialist.category?.toLowerCase().includes(q) ||
      specialist.companyName?.toLowerCase().includes(q) ||
      specialist.title?.toLowerCase().includes(q) ||
      specialist.expertise?.toLowerCase().includes(q);
    const matchesCountry = !selectedCountry || specialist.country?.toLowerCase() === selectedCountry.toLowerCase();
    const matchesStatus = !selectedStatus || specialist.status?.toLowerCase() === selectedStatus.toLowerCase();
    const matchesCategory = !selectedCategory || 
      specialist.category?.toLowerCase().includes(selectedCategory.toLowerCase());
    return matchesSearch && matchesCountry && matchesStatus && matchesCategory;
  });

  // Pagination
  const totalPages = Math.ceil(filteredSpecialists.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSpecialists = filteredSpecialists.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedCountry, selectedStatus, selectedCategory]);

  // Close dropdowns on outside click or scroll
  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('.asm-action-dropdown')) {
        setActionDropdown({ show: false, specialistId: null, x: 0, y: 0 });
      }
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setShowCategoryDropdown(false);
      }
    };
    const handleScroll = () => {
      setActionDropdown(prev => prev.show ? { show: false, specialistId: null, x: 0, y: 0 } : prev);
    };
    document.addEventListener('click', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  // Handle select item
  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Handle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(paginatedSpecialists.map(s => s.id));
    } else {
      setSelectedItems([]);
    }
  };

  // Handle status change
  const handleStatusChange = async (specialistId, newStatus) => {
    try {
      await specialistsApi.updateStatus(specialistId, newStatus);
      fetchSpecialists();
      setActionDropdown({ show: false, specialistId: null, x: 0, y: 0 });
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  // Handle delete specialist
  const handleDeleteSpecialist = async (specialistId) => {
    if (window.confirm('Are you sure you want to delete this specialist?')) {
      try {
        await specialistsApi.delete(specialistId);
        fetchSpecialists();
        setActionDropdown({ show: false, specialistId: null, x: 0, y: 0 });
      } catch (err) {
        console.error('Error deleting specialist:', err);
        alert('Failed to delete specialist');
      }
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedItems.length} specialists?`)) {
      try {
        await specialistsApi.bulkDelete(selectedItems);
        setSelectedItems([]);
        fetchSpecialists();
      } catch (err) {
        console.error('Error bulk deleting:', err);
        alert('Failed to delete specialists');
      }
    }
  };

  // Handle bulk status update
  const handleBulkStatusUpdate = async (status) => {
    if (selectedItems.length === 0) return;
    try {
      await specialistsApi.bulkUpdateStatus(selectedItems, status);
      setSelectedItems([]);
      fetchSpecialists();
    } catch (err) {
      console.error('Error bulk updating:', err);
      alert('Failed to update specialists');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const formatDateLong = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Get membership plan
  const getMembershipPlan = (specialist) => {
    if (specialist.membershipPlan) {
      return specialist.membershipPlan.charAt(0).toUpperCase() + specialist.membershipPlan.slice(1);
    }
    return 'Basic';
  };

  // Handle notes modal
  const handleNotesModal = (specialist) => {
    setActionDropdown({ show: false, specialistId: null, x: 0, y: 0 });
    setNotesSpecialist(specialist);
    setSpecialistNotes(specialist.bio || '');
    setShowNotesModal(true);
  };

  // Open details modal
  const openDetailsModal = async (specialist) => {
    setShowDetailsModal(true);
    setDetailsLoading(true);
    setDetailsSpecialist(null);
    try {
      const data = await specialistsApi.getById(specialist.id);
      setDetailsSpecialist(data.specialist || data);
    } catch (err) {
      console.error('Error fetching specialist details:', err);
      setDetailsSpecialist(specialist);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Pagination range helper
  const getPaginationRange = () => {
    const range = [];
    const maxShow = 5;
    let start = Math.max(1, currentPage - Math.floor(maxShow / 2));
    let end = Math.min(totalPages, start + maxShow - 1);
    if (end - start + 1 < maxShow) start = Math.max(1, end - maxShow + 1);
    for (let i = start; i <= end; i++) range.push(i);
    return range;
  };

  return (
    <AdminLayout>
      <div className="admin-specialist-manage-content">
        <div className="asm-page-header">
          <div className="asm-page-title-section">
            <h1 className="asm-page-title">Specialist Manage</h1>
            <p className="asm-page-description">Easily manage specialist profiles, services, and client interactions from a single dashboard</p>
          </div>
          <div className="asm-header-actions">
            <button className="admin-add-new-btn" onClick={() => { resetCreateForm(); setShowCreateModal(true); }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>
              Add Specialist
            </button>
            {selectedItems.length > 0 && (
              <>
                <button className="asm-bulk-btn asm-bulk-active" onClick={() => handleBulkStatusUpdate('active')}>
                  Activate ({selectedItems.length})
                </button>
                <button className="asm-bulk-btn asm-bulk-suspend" onClick={() => handleBulkStatusUpdate('suspended')}>
                  Suspend ({selectedItems.length})
                </button>
                <button className="asm-bulk-btn asm-bulk-hide" onClick={() => handleBulkStatusUpdate('inactive')}>
                  Hide ({selectedItems.length})
                </button>
                <button className="asm-bulk-btn asm-bulk-delete" onClick={handleBulkDelete}>
                  Delete ({selectedItems.length})
                </button>
              </>
            )}
          </div>
        </div>

        {/* Specialist Lists Header */}
        <div className="asm-list-header">
          <h2 className="asm-list-title">Specialist Lists</h2>
          <div className="asm-list-controls">
            <div className="asm-search-box">
              <input 
                type="text" 
                placeholder="Search..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="9.16667" cy="9.16667" r="5.83333" stroke="#969696" strokeWidth="1.5"/>
                <path d="M14.166 14.1665L17.4993 17.4998" stroke="#969696" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            
            {/* Country Filter */}
            <div className="asm-select-wrapper">
              <select 
                className="asm-filter-select"
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
              >
                <option value="">Country</option>
                <option value="UAE">UAE</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="Qatar">Qatar</option>
                <option value="Kuwait">Kuwait</option>
                <option value="Bahrain">Bahrain</option>
                <option value="Oman">Oman</option>
                {uniqueCountries
                  .filter(c => !['UAE', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Bahrain', 'Oman'].includes(c))
                  .map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))
                }
              </select>
            </div>
            
            {/* Status Filter */}
            <div className="asm-select-wrapper">
              <select 
                className="asm-filter-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            
            {/* Category Filter Dropdown */}
            <div className="asm-category-filter-wrapper" ref={categoryRef}>
              <button 
                className="asm-category-filter-btn"
                onClick={(e) => { e.stopPropagation(); setShowCategoryDropdown(prev => !prev); }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M18.0171 3.86955C17.9209 3.64729 17.7615 3.45822 17.5587 3.32584C17.3559 3.19347 17.1186 3.12363 16.8764 3.12502H3.12644C2.88452 3.1255 2.64792 3.19617 2.44537 3.32846C2.24282 3.46076 2.08301 3.64899 1.98532 3.87032C1.88764 4.09165 1.85629 4.33658 1.89507 4.57538C1.93385 4.81418 2.04109 5.03659 2.20379 5.21565L2.21004 5.22268L7.50144 10.8727V16.875C7.50139 17.1013 7.56274 17.3233 7.67894 17.5174C7.79514 17.7115 7.96185 17.8704 8.16128 17.9772C8.36071 18.084 8.58539 18.1347 8.81136 18.1239C9.03733 18.113 9.25613 18.0411 9.44441 17.9156L11.9444 16.2485C12.1158 16.1343 12.2563 15.9796 12.3535 15.798C12.4507 15.6165 12.5015 15.4137 12.5014 15.2078V10.8727L17.7936 5.22268L17.7999 5.21565C17.9643 5.03741 18.0726 4.8147 18.1112 4.57531C18.1498 4.33592 18.1171 4.09045 18.0171 3.86955Z" fill="#333333"/>
                </svg>
                {selectedCategory || 'Filter By Category'}
              </button>
              {showCategoryDropdown && (
                <div className="asm-category-dropdown">
                  <div 
                    className={`asm-category-dropdown-item ${!selectedCategory ? 'active' : ''}`} 
                    onClick={() => { setSelectedCategory(''); setShowCategoryDropdown(false); }}
                  >
                    All Categories
                  </div>
                  {allCategories.map(cat => (
                    <div
                      key={cat}
                      className={`asm-category-dropdown-item ${selectedCategory === cat ? 'active' : ''}`}
                      onClick={() => { setSelectedCategory(cat); setShowCategoryDropdown(false); }}
                    >
                      {cat}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="asm-content">
          {loading ? (
            <div className="asm-loading">
              <div className="asm-spinner"></div>
              <p>Loading specialists...</p>
            </div>
          ) : error ? (
            <div className="asm-error">{error} <button onClick={fetchSpecialists}>Retry</button></div>
          ) : (
            <>
              {/* Table */}
              <div className="asm-table-wrapper">
                <table className="asm-table">
                  <colgroup>
                    <col style={{ width: '50px' }} />
                    <col style={{ width: '55px' }} />
                    <col style={{ width: '100px' }} />
                    <col style={{ width: '150px' }} />
                    <col style={{ width: '120px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '170px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '60px' }} />
                    <col style={{ width: '55px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="asm-th-check">
                        <input 
                          type="checkbox" 
                          checked={paginatedSpecialists.length > 0 && selectedItems.length === paginatedSpecialists.length}
                          onChange={handleSelectAll}
                        />
                        <span>All</span>
                      </th>
                      <th>Avatar</th>
                      <th>Creation date</th>
                      <th>Specialist Name</th>
                      <th>Category</th>
                      <th>Location</th>
                      <th>Experience</th>
                      <th>Email</th>
                      <th>Company</th>
                      <th>Membership</th>
                      <th>Status</th>
                      <th>Details</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSpecialists.length > 0 ? (
                      paginatedSpecialists.map(specialist => (
                        <tr key={specialist.id} className="asm-row">
                          <td className="asm-td-check">
                            <input 
                              type="checkbox" 
                              checked={selectedItems.includes(specialist.id)}
                              onChange={() => handleSelectItem(specialist.id)}
                            />
                          </td>
                          <td className="asm-td-avatar">
                            {specialist.avatar ? (
                              <img 
                                src={getImageUrl(specialist.avatar)} 
                                alt={specialist.name}
                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                              />
                            ) : null}
                            <div className="asm-avatar-placeholder" style={specialist.avatar ? { display: 'none' } : {}}>
                              {specialist.name?.charAt(0) || 'S'}
                            </div>
                          </td>
                          <td className="asm-td-date">{formatDate(specialist.createdAt)}</td>
                          <td className="asm-td-name">{specialist.name || 'N/A'}</td>
                          <td className="asm-td-category">{specialist.category || 'N/A'}</td>
                          <td className="asm-td-location">{specialist.country || specialist.city || 'N/A'}</td>
                          <td className="asm-td-experience">{specialist.experience || 'N/A'}</td>
                          <td className="asm-td-email">{specialist.email || 'N/A'}</td>
                          <td className="asm-td-company">{specialist.companyName || 'N/A'}</td>
                          <td className="asm-td-membership">{getMembershipPlan(specialist)}</td>
                          <td className="asm-td-status">
                            <span className={`asm-status-badge ${(specialist.status || 'pending').toLowerCase()}`}>
                              {specialist.status ? specialist.status.charAt(0).toUpperCase() + specialist.status.slice(1) : 'Pending'}
                            </span>
                          </td>
                          <td className="asm-td-details">
                            <button className="asm-details-link" onClick={() => openDetailsModal(specialist)}>Details</button>
                          </td>
                          <td className="asm-td-action">
                            <div className="asm-action-dropdown">
                              <button 
                                className="asm-more-btn"
                                onClick={(e) => { 
                                  e.stopPropagation();
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setActionDropdown({ 
                                    show: actionDropdown.specialistId === specialist.id ? !actionDropdown.show : true, 
                                    specialistId: specialist.id,
                                    x: rect.right,
                                    y: rect.bottom + 4
                                  }); 
                                }}
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                  <circle cx="12" cy="5" r="2" fill="#6D6D6D"/>
                                  <circle cx="12" cy="12" r="2" fill="#6D6D6D"/>
                                  <circle cx="12" cy="19" r="2" fill="#6D6D6D"/>
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="13" className="asm-no-data">
                          {debouncedSearch || selectedCountry || selectedStatus || selectedCategory
                            ? 'No specialists match your filters.'
                            : 'No specialists found.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
              )}
            </>
          )}
        </div>
      </div>

      {/* ===== FIXED ACTION DROPDOWN OVERLAY ===== */}
      {actionDropdown.show && (() => {
        const specialist = specialists.find(s => s.id === actionDropdown.specialistId);
        if (!specialist) return null;
        return (
          <>
            <div className="asm-action-overlay-backdrop" onClick={() => setActionDropdown({ show: false, specialistId: null, x: 0, y: 0 })} />
            <div 
              className="asm-action-menu-fixed" 
              style={{ top: actionDropdown.y, left: actionDropdown.x - 170 }}
            >
              <div className="asm-action-item" onClick={() => { setActionDropdown({ show: false, specialistId: null, x: 0, y: 0 }); openDetailsModal(specialist); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#333" strokeWidth="2"/><circle cx="12" cy="12" r="3" stroke="#333" strokeWidth="2"/></svg>
                View
              </div>
              <div className="asm-action-item" onClick={() => { setActionDropdown({ show: false, specialistId: null, x: 0, y: 0 }); setEditSpecialist(specialist); setShowEditModal(true); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Edit
              </div>
              {specialist.status?.toLowerCase() !== 'active' && specialist.status?.toLowerCase() !== 'suspended' && (
                <div className="asm-action-item" onClick={() => handleStatusChange(specialist.id, 'active')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="22 4 12 14.01 9 11.01" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Approve
                </div>
              )}
              {specialist.status?.toLowerCase() === 'suspended' && (
                <div className="asm-action-item asm-action-unsuspend" onClick={() => handleStatusChange(specialist.id, 'active')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="22 4 12 14.01 9 11.01" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Unsuspend
                </div>
              )}
              {specialist.status?.toLowerCase() !== 'inactive' && (
                <div className="asm-action-item" onClick={() => handleStatusChange(specialist.id, 'inactive')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Hide
                </div>
              )}
              {specialist.status?.toLowerCase() !== 'pending' && (
                <div className="asm-action-item" onClick={() => handleStatusChange(specialist.id, 'pending')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#f59e0b" strokeWidth="2"/><polyline points="12 6 12 12 16 14" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Set Pending
                </div>
              )}
              <div className="asm-action-item" onClick={() => handleNotesModal(specialist)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="14 2 14 8 20 8" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="16" y1="13" x2="8" y2="13" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="16" y1="17" x2="8" y2="17" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Notes
              </div>
              {specialist.status?.toLowerCase() !== 'suspended' && (
                <div className="asm-action-item asm-action-suspend" onClick={() => handleStatusChange(specialist.id, 'suspended')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#f97316" strokeWidth="2"/><line x1="4" y1="4" x2="20" y2="20" stroke="#f97316" strokeWidth="2" strokeLinecap="round"/></svg>
                  Suspend
                </div>
              )}
              <div className="asm-action-item asm-action-delete" onClick={() => handleDeleteSpecialist(specialist.id)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><polyline points="3 6 5 6 21 6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Delete
              </div>
            </div>
          </>
        );
      })()}

      {/* ===== DETAILS MODAL ===== */}
      {showDetailsModal && (
        <div className="asm-modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="asm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="asm-modal-header">
              <h3>Specialist Details</h3>
              <button className="asm-modal-close" onClick={() => setShowDetailsModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="asm-modal-body">
              {detailsLoading ? (
                <div className="asm-loading" style={{ padding: '40px 0' }}>
                  <div className="asm-spinner"></div>
                  <p>Loading details...</p>
                </div>
              ) : detailsSpecialist ? (
                <div className="asm-details-content">
                  {/* Profile Header */}
                  <div className="asm-details-profile">
                    <div className="asm-details-avatar">
                      {detailsSpecialist.avatar ? (
                        <img src={getImageUrl(detailsSpecialist.avatar)} alt={detailsSpecialist.name} />
                      ) : (
                        <div className="asm-details-avatar-placeholder">
                          {detailsSpecialist.name?.charAt(0) || 'S'}
                        </div>
                      )}
                    </div>
                    <div className="asm-details-profile-info">
                      <h4 className="asm-details-name">{detailsSpecialist.name || 'N/A'}</h4>
                      <p className="asm-details-title-text">{detailsSpecialist.title || detailsSpecialist.category || 'Specialist'}</p>
                      <span className={`asm-status-badge ${(detailsSpecialist.status || 'pending').toLowerCase()}`}>
                        {detailsSpecialist.status ? detailsSpecialist.status.charAt(0).toUpperCase() + detailsSpecialist.status.slice(1) : 'Pending'}
                      </span>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="asm-details-grid">
                    <div className="asm-details-item">
                      <span className="asm-details-label">Email</span>
                      <span className="asm-details-value">{detailsSpecialist.email || 'N/A'}</span>
                    </div>
                    <div className="asm-details-item">
                      <span className="asm-details-label">Phone</span>
                      <span className="asm-details-value">{detailsSpecialist.phone || 'N/A'}</span>
                    </div>
                    <div className="asm-details-item">
                      <span className="asm-details-label">Category</span>
                      <span className="asm-details-value">{detailsSpecialist.category || 'N/A'}</span>
                    </div>
                    <div className="asm-details-item">
                      <span className="asm-details-label">Experience</span>
                      <span className="asm-details-value">{detailsSpecialist.experience || 'N/A'}</span>
                    </div>
                    <div className="asm-details-item">
                      <span className="asm-details-label">Country</span>
                      <span className="asm-details-value">{detailsSpecialist.country || 'N/A'}</span>
                    </div>
                    <div className="asm-details-item">
                      <span className="asm-details-label">City</span>
                      <span className="asm-details-value">{detailsSpecialist.city || 'N/A'}</span>
                    </div>
                    <div className="asm-details-item">
                      <span className="asm-details-label">Company</span>
                      <span className="asm-details-value">{detailsSpecialist.companyName || 'N/A'}</span>
                    </div>
                    <div className="asm-details-item">
                      <span className="asm-details-label">Membership</span>
                      <span className="asm-details-value">{getMembershipPlan(detailsSpecialist)}</span>
                    </div>
                    <div className="asm-details-item">
                      <span className="asm-details-label">Expertise</span>
                      <span className="asm-details-value">{detailsSpecialist.expertise || 'N/A'}</span>
                    </div>
                    <div className="asm-details-item">
                      <span className="asm-details-label">Interested Locations</span>
                      <span className="asm-details-value">{detailsSpecialist.interestedLocations || 'N/A'}</span>
                    </div>
                    <div className="asm-details-item">
                      <span className="asm-details-label">Email Verified</span>
                      <span className="asm-details-value">{detailsSpecialist.isEmailVerified ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="asm-details-item">
                      <span className="asm-details-label">Joined</span>
                      <span className="asm-details-value">{formatDateLong(detailsSpecialist.createdAt)}</span>
                    </div>
                    <div className="asm-details-item">
                      <span className="asm-details-label">Last Login</span>
                      <span className="asm-details-value">{formatDateLong(detailsSpecialist.lastLoginAt)}</span>
                    </div>
                  </div>

                  {/* Bio */}
                  {detailsSpecialist.bio && (
                    <div className="asm-details-bio">
                      <span className="asm-details-label">Bio</span>
                      <p>{detailsSpecialist.bio}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>Specialist not found.</p>
              )}
            </div>
            <div className="asm-modal-footer">
              <button className="asm-modal-btn-secondary" onClick={() => setShowDetailsModal(false)}>Close</button>
              {detailsSpecialist && (
                <button
                  className="asm-modal-btn-primary"
                  onClick={() => {
                    setShowDetailsModal(false);
                    if (isAdmin) {
                      navigate(`/admin/specialists/${detailsSpecialist.id}`);
                    } else {
                      navigate(`/professional/${detailsSpecialist.id}`);
                    }
                  }}
                >
                  View Full Profile
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== NOTES MODAL ===== */}
      {showNotesModal && (
        <div className="asm-modal-overlay" onClick={() => setShowNotesModal(false)}>
          <div className="asm-modal asm-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="asm-modal-header">
              <h3>Bio — {notesSpecialist?.name || 'Specialist'}</h3>
              <button className="asm-modal-close" onClick={() => setShowNotesModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="asm-modal-body">
              <textarea
                className="asm-notes-textarea"
                value={specialistNotes}
                onChange={(e) => setSpecialistNotes(e.target.value)}
                placeholder="Specialist bio..."
                rows={6}
                readOnly
              />
            </div>
            <div className="asm-modal-footer">
              <button className="asm-modal-btn-secondary" onClick={() => setShowNotesModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Specialist Modal */}
      {showCreateModal && (
        <div className="admin-create-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="admin-create-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-create-modal-header">
              <h3>Add New Specialist</h3>
              <button className="admin-create-modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="admin-create-modal-body">
              <div className="admin-create-form-row">
                <div className="admin-create-form-group">
                  <label>Full Name <span className="required">*</span></label>
                  <input value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} placeholder="Full name" />
                  {createErrors.name && <div className="admin-create-form-error">{createErrors.name}</div>}
                </div>
                <div className="admin-create-form-group">
                  <label>Email <span className="required">*</span></label>
                  <input type="email" value={createForm.email} onChange={e => setCreateForm({...createForm, email: e.target.value})} placeholder="Email address" />
                  {createErrors.email && <div className="admin-create-form-error">{createErrors.email}</div>}
                </div>
              </div>
              <div className="admin-create-form-row">
                <div className="admin-create-form-group">
                  <label>Phone</label>
                  <input value={createForm.phone} onChange={e => setCreateForm({...createForm, phone: e.target.value})} placeholder="Phone number" />
                </div>
                <div className="admin-create-form-group">
                  <label>Title</label>
                  <input value={createForm.title} onChange={e => setCreateForm({...createForm, title: e.target.value})} placeholder="e.g. Senior Architect" />
                </div>
              </div>
              <div className="admin-create-form-row">
                <div className="admin-create-form-group">
                  <label>Category</label>
                  <select value={createForm.category} onChange={e => setCreateForm({...createForm, category: e.target.value})}>
                    <option value="">Select category</option>
                    {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="admin-create-form-group">
                  <label>Experience (years)</label>
                  <input type="number" value={createForm.experience} onChange={e => setCreateForm({...createForm, experience: e.target.value})} placeholder="Years" />
                </div>
              </div>
              <div className="admin-create-form-group">
                <label>Expertise</label>
                <input value={createForm.expertise} onChange={e => setCreateForm({...createForm, expertise: e.target.value})} placeholder="e.g. Residential, Commercial" />
              </div>
              <div className="admin-create-form-row">
                <div className="admin-create-form-group">
                  <label>Country</label>
                  <input value={createForm.country} onChange={e => setCreateForm({...createForm, country: e.target.value})} placeholder="Country" />
                </div>
                <div className="admin-create-form-group">
                  <label>City</label>
                  <input value={createForm.city} onChange={e => setCreateForm({...createForm, city: e.target.value})} placeholder="City" />
                </div>
              </div>
              <div className="admin-create-form-group">
                <label>Company Name</label>
                <input value={createForm.companyName} onChange={e => setCreateForm({...createForm, companyName: e.target.value})} placeholder="Company name" />
              </div>
              <div className="admin-create-form-group">
                <label>Bio</label>
                <textarea value={createForm.bio} onChange={e => setCreateForm({...createForm, bio: e.target.value})} placeholder="Brief biography" />
              </div>
              {createErrors.submit && <div className="admin-create-form-error">{createErrors.submit}</div>}
            </div>
            <div className="admin-create-modal-footer">
              <button className="admin-create-cancel-btn" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="admin-create-save-btn" onClick={handleCreateSpecialist} disabled={createSaving}>{createSaving ? 'Creating...' : 'Create Specialist'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== EDIT MODAL ===== */}
      <SpecialistEditModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setEditSpecialist(null); }}
        specialist={editSpecialist}
        onUpdate={fetchSpecialists}
      />
    </AdminLayout>
  );
};

export default AdminSpecialistPage;
